import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import {
  AlertColor,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import DateInput from 'components/DateInput/DateInput';
import { FollowUpTimeline, type FollowUpEntry } from 'components/FollowUpTimeline';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import dayjs from 'dayjs';
import { FC, useMemo, useRef, useState } from 'react';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { BookingDetailSectionTitle } from './BookingDetailSectionTitle';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

type DropdownOption = { value: any; label: any };

interface BookingFollowUpsProps {
  bookingLineItemId: any;
  bookingLineItem: any;
  followUps: FollowUpEntry[];
  assigneeList: DropdownOption[];
  followUpModes: DropdownOption[];
  restrictPriceOverride: boolean;
  getInitialResponse: () => Promise<void> | void;
}

const BookingFollowUps: FC<BookingFollowUpsProps> = ({
  bookingLineItemId,
  bookingLineItem,
  followUps,
  assigneeList,
  followUpModes,
  restrictPriceOverride,
  getInitialResponse,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [approval, setApproval] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpMode, setFollowUpMode] = useState<DropdownOption | null>(null);
  const [assignedTo, setAssignedTo] = useState<DropdownOption | null>(null);
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const orderedFollowUps = useMemo(
    () =>
      [...(followUps ?? [])].sort(
        (a, b) =>
          dayjs(b.createdAt || b.datetime).valueOf() - dayjs(a.createdAt || a.datetime).valueOf(),
      ),
    [followUps],
  );

  const dateMissing = !approval && !followUpDate;
  const modeMissing = !approval && !followUpMode?.value;
  const remarksMissing = !remarks.trim();
  const isDeleted = Boolean(bookingLineItem?.deleted);

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    event.target.value = '';
    if (!next) return;
    if (next.size > MAX_FILE_BYTES) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.booking_logs_file_too_large,
      });
      return;
    }
    setFile(next);
  };

  const resetForm = () => {
    setFollowUpDate('');
    setFollowUpMode(null);
    setAssignedTo(null);
    setRemarks('');
    setFile(null);
    setSubmitted(false);
    setApproval(false);
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (isDeleted) return;
    if (remarksMissing || (!approval && (dateMissing || modeMissing))) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('followUpDate', approval ? dayjs().format('YYYY-MM-DD') : followUpDate);
      formData.append('followUpMode', approval ? 'PRICE_APPROVAL' : String(followUpMode?.value));
      if (!approval && assignedTo?.value) {
        formData.append('assignedTo', String(assignedTo.value));
      }
      formData.append('description', remarks.trim());
      if (file) {
        formData.append('file', file);
      } else {
        formData.append('file', null as any);
      }
      const { status } = await axiosInstance.post(URLS.bookingLogs(bookingLineItemId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.booking_logs_follow_up_added,
        });
        resetForm();
        await getInitialResponse();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {isLoading && <PageLoader />}

      {!isDeleted && (
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            px: 1.75,
            py: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper',
          }}>
          <BookingDetailSectionTitle icon={AddTaskOutlinedIcon} sx={{ mb: 1.25 }}>
            {strings.booking_logs_add_follow_up}
          </BookingDetailSectionTitle>
          {restrictPriceOverride && (
            <FormControlLabel
              sx={{ mb: 1, ml: 0 }}
              control={
                <Checkbox
                  checked={approval}
                  onChange={() => {
                    setApproval(prev => !prev);
                    setSubmitted(false);
                  }}
                  color="primary"
                  size="small"
                />
              }
              label={strings.booking_logs_price_override}
            />
          )}
          <Grid container spacing={1.5} alignItems="stretch">
            {!approval && (
              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    width: '100%',
                    flex: 1,
                    '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' },
                    '& .MuiFormHelperText-root:not(.Mui-error)': { display: 'none', m: 0 },
                  }}>
                  <DateInput
                    field="followUpDate"
                    label={`${strings.followUpDate} *`}
                    value={followUpDate ? dayjs(followUpDate) : null}
                    minDate={dayjs().startOf('day')}
                    textChange={val => setFollowUpDate(val || '')}
                    onChange={() => undefined}
                    size="small"
                    hasError={submitted}
                    fieldError={submitted && dateMissing}
                    validators={[]}
                  />
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={followUpModes}
                    value={followUpMode}
                    onChange={(_e, next) => setFollowUpMode(next)}
                    getOptionLabel={option => String(option?.label ?? '')}
                    isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        required
                        label={strings.sales_call_follow_up_mode}
                        error={submitted && modeMissing}
                        helperText={submitted && modeMissing ? strings.requiredText : undefined}
                      />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={assigneeList}
                    value={assignedTo}
                    onChange={(_e, next) => setAssignedTo(next)}
                    getOptionLabel={option => String(option?.label ?? '')}
                    isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                    renderInput={params => (
                      <TextField {...params} label={strings.lead_management_contact_details_assignedTo} />
                    )}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={approval ? 12 : 6} sx={{ display: 'flex' }}>
              <TextField
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                label={strings.booking_logs_discussion}
                placeholder={strings.booking_logs_discussion_placeholder}
                multiline
                minRows={approval ? 6 : 3}
                fullWidth
                size="small"
                required
                error={submitted && remarksMissing}
                helperText={submitted && remarksMissing ? strings.requiredText : undefined}
                sx={{
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '& .MuiInputBase-root': {
                    flex: 1,
                    alignItems: 'stretch',
                    height: '100%',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  },
                }}
              />
            </Grid>
          </Grid>
          <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mt: 1.25,
              flexWrap: 'wrap',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}>
                {strings.booking_logs_attach_file}
              </Button>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{strings.booking_logs_max_file_size}</Typography>
              {file ? (
                <Chip
                  size="small"
                  label={file.name}
                  onDelete={() => setFile(null)}
                  deleteIcon={<CloseIcon />}
                  sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
              ) : null}
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isLoading}
              sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto' }}>
              {strings.booking_logs_save_follow_up}
            </Button>
          </Box>
        </Paper>
      )}

      <FollowUpTimeline followUps={orderedFollowUps} onComplete={getInitialResponse} />
    </>
  );
};

export default BookingFollowUps;
